import { useEffect, useId, useMemo, useRef, useState, type MouseEvent, type ReactNode } from 'react';
import { useT } from '../i18n/index.js';
import { coincide, normalizar, puntaje } from '../lib/texto.js';
import { Ico } from './Iconos.js';

/** The names the list under the field draws at a time: fifty more as it is scrolled near its foot. */
const A_LA_VEZ = 50;

type Renglon =
  | { tipo: 'ninguno'; nombre: string }
  | { tipo: 'opcion'; id: string; nombre: string }
  | { tipo: 'nuevo'; nombre: string };

/**
 * A supplier or a client picked by typing (the owner, 2026-10-12: «wouldn't this be better as an
 * autocomplete typable field instead? if you click on the field it should show all the suppliers
 * in alphabetical order … and still have the option for "new supplier"»). It was a dropdown of all
 * of them in the order they were added. Tapped, the field lists every one by name; typed into, what
 * matches, accents aside, best first; the arrows and Enter pick as a tap does. A new one is the
 * list's foot, always in sight, with what was typed for its name. Picked, the field says the name;
 * left with words that are no name on the list, it goes back to the one picked.
 */
export function ElegirEscribiendo({
  etiqueta,
  placeholder,
  opciones,
  value,
  onChange,
  ninguno,
  nuevo,
  libre = false,
  maxLength,
  datos,
  ayuda,
}: {
  etiqueta: string;
  placeholder: string;
  /** What can be picked, in any order: the field lists them by name. */
  opciones: readonly { id: string; nombre: string }[];
  /** The one picked, by id; empty for none. */
  value: string;
  onChange: (id: string) => void;
  /** Where none at all can be the answer, what it says («Nadie en particular»): first on the list. */
  ninguno?: string;
  /**
   * A new one: `etiqueta` says it with what was typed («» for a name already on the list), and
   * `onNuevo` starts its form with that. An empty `etiqueta` leaves the row out.
   */
  nuevo?: { etiqueta: (escrito: string) => string; onNuevo: (escrito: string) => void };
  /**
   * What is typed is the value, as in a plain field, the list only a help (a product's category): a
   * name on it, typed whole, becomes that one's; any other is kept as typed.
   */
  libre?: boolean;
  /** Where what is typed is kept, as long as the record takes. */
  maxLength?: number;
  /** For a test to find the field. */
  datos?: string;
  ayuda?: ReactNode;
}) {
  const t = useT();
  const id = useId();
  const envoltura = useRef<HTMLDivElement>(null);
  const campo = useRef<HTMLInputElement>(null);
  const [abierta, setAbierta] = useState(false);
  /** What was typed since the field took the cursor; null while it says the one picked. */
  const [escrito, setEscrito] = useState<string | null>(null);
  const [activa, setActiva] = useState(0);
  const [cuantas, setCuantas] = useState(A_LA_VEZ);
  /** A pick being made: the blur that follows it has nothing left to settle. */
  const eligiendo = useRef(false);

  const elegida = opciones.find((o) => o.id === value);
  const porNombre = useMemo(() => [...opciones].sort((a, b) => a.nombre.localeCompare(b.nombre, 'es')), [opciones]);
  const consulta = escrito?.trim() ?? '';
  // Sorted from the list by name: what scores the same stays in alphabetical order.
  const halladas = useMemo(
    () => (consulta ? porNombre.filter((o) => coincide(o.nombre, consulta)).sort((a, b) => puntaje(a.nombre, consulta) - puntaje(b.nombre, consulta)) : porNombre),
    [porNombre, consulta],
  );
  const conNinguno = ninguno !== undefined && !consulta;
  /** The one whose name, or value, was typed whole: no new one is offered under it. */
  const exacta = consulta ? exactaDe(opciones, consulta) : undefined;
  const escritoNuevo = exacta ? '' : consulta;
  const textoNuevo = nuevo?.etiqueta(escritoNuevo) ?? '';
  const renglones: Renglon[] = [
    ...(conNinguno ? [{ tipo: 'ninguno' as const, nombre: ninguno }] : []),
    ...halladas.slice(0, cuantas).map((o) => ({ tipo: 'opcion' as const, id: o.id, nombre: o.nombre })),
    ...(textoNuevo ? [{ tipo: 'nuevo' as const, nombre: textoNuevo }] : []),
  ];
  /** Where the names drawn end: past it, with more to draw, the arrows draw the next ones. */
  const finDeLasDibujadas = (conNinguno ? 1 : 0) + Math.min(cuantas, halladas.length);

  // The one the arrows reached stays in sight, the box scrolled and never the page, above the new
  // one's row, which keeps to the box's foot.
  useEffect(() => {
    const caja = document.getElementById(`${id}-lista`);
    const op = document.getElementById(`${id}-${activa}`);
    if (!abierta || !caja || !op) return;
    const pie = op.dataset.nuevo === undefined ? (caja.querySelector<HTMLElement>('[data-nuevo]')?.offsetHeight ?? 0) : 0;
    if (op.offsetTop < caja.scrollTop) caja.scrollTop = op.offsetTop;
    else if (op.offsetTop + op.offsetHeight > caja.scrollTop + caja.clientHeight - pie)
      caja.scrollTop = op.offsetTop + op.offsetHeight - caja.clientHeight + pie;
  }, [abierta, activa, id]);

  function abrir() {
    if (abierta) return;
    const i = elegida ? porNombre.findIndex((o) => o.id === elegida.id) : -1;
    setCuantas(Math.max(A_LA_VEZ, i + 1));
    setActiva(i >= 0 ? i + (ninguno !== undefined ? 1 : 0) : 0);
    setAbierta(true);
    // On a phone the keyboard takes the screen's lower half, the list's place: the field goes up.
    if (window.matchMedia?.('(pointer: coarse)').matches) {
      const reducido = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      setTimeout(() => envoltura.current?.scrollIntoView({ block: 'start', behavior: reducido ? 'auto' : 'smooth' }), 250);
    }
  }
  function elegir(r: Renglon) {
    eligiendo.current = true;
    if (r.tipo === 'nuevo') nuevo?.onNuevo(escritoNuevo);
    else onChange(r.tipo === 'opcion' ? r.id : '');
    setEscrito(null);
    setAbierta(false);
    campo.current?.blur();
  }
  /**
   * Left without a pick: a name typed whole is that one, nothing typed is none where none can be;
   * where what is typed is the value (`libre`), anything else stays as typed, spaces tidied.
   */
  function soltar() {
    setAbierta(false);
    if (eligiendo.current) {
      eligiendo.current = false;
      return;
    }
    if (escrito !== null) {
      const igual = exactaDe(opciones, escrito);
      if (igual) onChange(igual.id);
      else if (libre) onChange(escrito.trim().replace(/\s+/g, ' '));
      else if (!escrito.trim() && ninguno !== undefined) onChange('');
    }
    setEscrito(null);
  }

  const renglon = (r: Renglon, i: number) => {
    const comun = {
      id: `${id}-${i}`,
      role: 'option',
      'aria-selected': i === activa,
      // Picked before the field loses the cursor, or the list is gone under the finger.
      onMouseDown: (e: MouseEvent) => e.preventDefault(),
      onClick: () => elegir(r),
    } as const;
    if (r.tipo === 'nuevo')
      return (
        <li
          key="nuevo"
          {...comun}
          data-nuevo=""
          className={`sticky bottom-0 -mx-1 -mb-1 flex cursor-pointer items-center gap-2 border-t border-slate-700 px-4 py-3 text-sm font-medium text-marca-300 ${i === activa ? 'bg-slate-800' : 'bg-slate-900 hover:bg-slate-800'}`}
        >
          <Ico icono="agregar" className="size-4 shrink-0" />
          {/* Whole, on two lines if it takes them: the name typed is what it is about. */}
          <span className="min-w-0 wrap-break-word">{r.nombre}</span>
        </li>
      );
    const esta = r.tipo === 'opcion' ? r.id === value : value === '';
    return (
      <li
        key={r.tipo === 'opcion' ? r.id : 'ninguno'}
        {...comun}
        className={`flex cursor-pointer items-center justify-between gap-2 rounded-lg px-3 py-2.5 text-sm ${i === activa ? 'bg-slate-800 text-white' : r.tipo === 'ninguno' ? 'text-slate-400 hover:bg-slate-800/60' : 'text-slate-200 hover:bg-slate-800/60'}`}
      >
        <span className="min-w-0 wrap-break-word">{r.nombre}</span>
        {esta && <Ico icono="marca" className="size-4 shrink-0 text-marca-400" />}
      </li>
    );
  };

  return (
    <div ref={envoltura} className="scroll-mt-4" data-elegir-escribiendo={datos}>
      <label htmlFor={`${id}-campo`} className="etiqueta">
        {etiqueta}
      </label>
      <div className="relative">
        <input
          ref={campo}
          id={`${id}-campo`}
          role="combobox"
          aria-expanded={abierta}
          aria-controls={`${id}-lista`}
          aria-autocomplete="list"
          aria-activedescendant={abierta && renglones[activa] ? `${id}-${activa}` : undefined}
          className="campo pr-11"
          value={escrito ?? elegida?.nombre ?? (libre ? value : '')}
          placeholder={placeholder}
          autoComplete="off"
          maxLength={maxLength}
          enterKeyHint="done"
          onFocus={(e) => {
            abrir();
            // Typing starts over the name picked, not after it.
            e.currentTarget.select();
          }}
          onClick={(e) => {
            abrir();
            // Again once the tap is done: lifting the finger put the cursor where it landed.
            if (escrito === null) e.currentTarget.select();
          }}
          onBlur={soltar}
          onChange={(e) => {
            setEscrito(e.target.value);
            setAbierta(true);
            setActiva(0);
            setCuantas(A_LA_VEZ);
            if (libre) onChange(e.target.value);
          }}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') {
              e.preventDefault();
              if (!abierta) return abrir();
              const sig = activa + 1;
              if (sig >= finDeLasDibujadas && cuantas < halladas.length) {
                setCuantas(cuantas + A_LA_VEZ);
                setActiva(sig);
              } else setActiva(Math.min(sig, renglones.length - 1));
            } else if (e.key === 'ArrowUp') {
              e.preventDefault();
              setActiva(Math.max(activa - 1, 0));
            } else if (e.key === 'Enter') {
              const r = renglones[activa];
              // Never the form's own Enter while the list is open: that would save it half-filled.
              if (abierta && r) {
                e.preventDefault();
                elegir(r);
              }
            } else if (e.key === 'Escape' && abierta) {
              // The list closes, and the sheet the field is in stays.
              e.preventDefault();
              e.stopPropagation();
              setAbierta(false);
              setEscrito(null);
            }
          }}
        />
        <Ico icono="desplegar" className="pointer-events-none absolute top-1/2 right-4 size-5 -translate-y-1/2 text-slate-500" />
        {abierta && (
          <ul
            id={`${id}-lista`}
            role="listbox"
            aria-label={etiqueta}
            // In a lesson the spotlight takes it in with the field: under the dimming no tap could pick from it.
            data-guia-desborde
            // Never narrower than a name: a field half a row wide («Carga rápida» on a phone) cut every one.
            className="absolute top-full right-0 left-0 z-20 mt-1 max-h-64 min-w-48 overflow-auto rounded-xl bg-slate-900 p-1 shadow-xl ring-1 ring-slate-700"
            onScroll={(e) => {
              const el = e.currentTarget;
              if (cuantas < halladas.length && el.scrollTop + el.clientHeight >= el.scrollHeight - 120) setCuantas(cuantas + A_LA_VEZ);
            }}
          >
            {renglones.filter((r) => r.tipo !== 'nuevo').map(renglon)}
            {consulta && halladas.length === 0 && (
              <li role="presentation" className="px-3 py-2.5 text-sm text-slate-500">
                {t('historial.ningunoSeLlamaAsi')}
              </li>
            )}
            {textoNuevo && renglon(renglones.at(-1)!, renglones.length - 1)}
          </ul>
        )}
      </div>
      {ayuda && <span className="mt-1.5 block text-xs text-slate-500">{ayuda}</span>}
    </div>
  );
}

/** The one called what was typed, accents, capitals and spacing aside, by name or by value: only if one alone. */
function exactaDe<T extends { id: string; nombre: string }>(opciones: readonly T[], escrito: string): T | undefined {
  const q = normalizar(escrito.replace(/\s+/g, ' '));
  if (!q) return undefined;
  const iguales = opciones.filter((o) => normalizar(o.nombre) === q || normalizar(o.id) === q);
  return iguales.length === 1 ? iguales[0] : undefined;
}
